import { Button, Card, Descriptions, Tag, Typography } from 'antd';
import { ApiOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Text className="ol-mono" style={{ fontSize: 11.5 }}>
      {children}
    </Text>
  );
}

export interface SignalsIngressCardProps {
  /** `alerts.rules.write` — whoever authors routing rules may test them. */
  canTestFire?: boolean;
  onTestSignal?: () => void;
}

/**
 * "How alerts get in" (OPSFL-ALUI-1) — an explainer over the TWO intakes the alert centre has
 * (FSD §7.1/§7.3, ratified contract lifecycle-signals-webhook.v1), plus the console test-fire
 * (OPSFL-FIX-1): a rule author can inject a synthetic signal through the REAL internal intake +
 * routing path — never through the HMAC rail (the console will not fake a sender's crypto), and
 * always marked `CONSOLE_TEST`. Signing keys stay provisioned by the implementation team
 * (derived per hub, shown to the partner once, never stored).
 */
export function SignalsIngressCard({ canTestFire = false, onTestSignal }: SignalsIngressCardProps) {
  return (
    <Card
      size="small"
      title={
        <span>
          <ApiOutlined style={{ marginRight: 8, color: 'var(--ol-primary)' }} aria-hidden />
          How alerts get in
        </span>
      }
      extra={
        canTestFire && onTestSignal ? (
          <Button size="small" icon={<ThunderboltOutlined />} onClick={onTestSignal}>
            Send test signal
          </Button>
        ) : null
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, maxWidth: 820 }}>
        Every alert lands in ONE durable register under a natural <Mono>alert_key</Mono>, so intake
        is idempotent — the same fact arriving twice is one alert, never two. There are two intakes:
      </Paragraph>

      <Paragraph style={{ fontSize: 12.5, marginBottom: 6 }}>
        <Tag style={{ marginInlineEnd: 8 }}>DERIVED</Tag>
        The platform&rsquo;s own conditions, materialised as they are read: SLA breaches, overdue
        reviews, reconciliation breaks (keys like <Mono>SLA_BREACH:&lt;itemId&gt;</Mono>) and
        auto-disabled webhook subscriptions. Nothing to configure — if the condition exists, the
        alert exists.
      </Paragraph>

      <Paragraph style={{ fontSize: 12.5 }}>
        <Tag style={{ marginInlineEnd: 8 }}>SIGNAL</Tag>
        External systems (transaction monitoring, screening, market data) post signals to the
        ratified machine rail below. Each landed signal raises an alert keyed{' '}
        <Mono>SIGNAL:&lt;signalId&gt;</Mono>; routing rules take over from there.
      </Paragraph>

      <Descriptions
        size="small"
        column={1}
        bordered
        items={[
          {
            key: 'endpoint',
            label: 'Endpoint',
            children: <Mono>POST /api/v1/signals</Mono>,
          },
          {
            key: 'auth',
            label: 'Authentication',
            children: (
              <span style={{ fontSize: 12 }}>
                HMAC-SHA256 over <Mono>timestamp + &quot;.&quot; + raw body</Mono>, constant-time
                compare, ±5-minute replay window. Headers: <Mono>X-OL-Key-Id</Mono> (
                <Mono>sig-&lt;hub&gt;-v&lt;n&gt;</Mono> — the key id IS the hub binding),{' '}
                <Mono>X-OL-Timestamp</Mono>, <Mono>X-OL-Signature</Mono>. No session, no bearer —
                this is a machine rail; per-hub secrets are issued by the implementation team and
                are never stored at rest.
              </span>
            ),
          },
          {
            key: 'body',
            label: 'Body',
            children: (
              <span style={{ fontSize: 12 }}>
                <Mono>
                  {'{ type, source, external_ref, payload_digest?, correlation_hints?, detail? }'}
                </Mono>{' '}
                — de-duplicated on <Mono>(hub, source, external_ref)</Mono>; the digest is
                recomputed server-side over the bytes that actually arrived.
              </span>
            ),
          },
          {
            key: 'responses',
            label: 'Responses',
            children: (
              <span style={{ fontSize: 12 }}>
                <Mono>202</Mono> landed (<Mono>{'{ signal_id, state, duplicate }'}</Mono>) ·{' '}
                <Mono>422</Mono> unknown or disabled type · <Mono>409</Mono> reserved type (
                <Mono>TM_ALERT</Mono> is refused, never silently dropped). Which types are
                admitted is the hub&rsquo;s signal catalogue — the Signal types panel below.
              </span>
            ),
          },
          {
            key: 'contract',
            label: 'Contract',
            children: (
              <span style={{ fontSize: 12 }}>
                <Mono>contracts/openapi/v1/lifecycle-signals-webhook.v1.md</Mono> (ratified) — the
                wire shape ships exactly as ratified, nothing invented.
              </span>
            ),
          },
        ]}
      />

      <Paragraph type="secondary" style={{ fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>
        The console never fires through this rail — it authenticates senders cryptographically,
        and this surface will not fake one. To verify an <i>integration</i>, have the sending
        system post a signal with a fresh <Mono>external_ref</Mono>. To verify <i>routing</i>,
        rule authors can use &ldquo;Send test signal&rdquo; above: it exercises the same internal
        intake and rules for real, and its alert is permanently marked{' '}
        <Mono>signal_source = CONSOLE_TEST</Mono>.
      </Paragraph>
    </Card>
  );
}
